import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/pages/Login";
import { api, errorText } from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { BrandMark } from "@/components/onboarding/BrandMark";
import { Field } from "@/components/onboarding/Field";
import { PlanPicker } from "@/components/onboarding/PlanPicker";
import { useBrandTitle } from "@/lib/useBrandTitle";

const EMPTY = { name: "", email: "", password: "", property_name: "", property_address: "" };

export default function PropertySignup() {
  const { slug } = useParams();
  const [brand, setBrand] = useState(slug ? null : false);
  const [form, setForm] = useState(EMPTY);
  const [plan, setPlan] = useState("purchase");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const accent = brand?.color || "#10B981";
  useBrandTitle(brand && brand !== "missing" ? brand : null);

  useEffect(() => {
    if (slug) api.get(`/portal/${slug}`).then((r) => setBrand(r.data)).catch(() => setBrand("missing"));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/signup/property", { ...form, plan, msp_slug: slug || null });
      await startCheckout();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  if (brand === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;
  if (brand === "missing") return (
    <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="portal-missing">
      <div><h1 className="text-2xl font-semibold text-slate-100">This sign-up link isn't active</h1>
        <p className="text-slate-400 text-sm mt-2">Please check the link with your service provider.</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#07090E] grid-bg">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex items-center justify-between gap-4">
          {brand ? <BrandMark brand={brand} /> : <Link to="/" data-testid="signup-brand-home-link"><Brand /></Link>}
          {!brand && <Link to="/" className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100 transition-colors shrink-0" data-testid="signup-back-home-link"><ArrowLeft className="h-4 w-4" />Home</Link>}
        </div>
        <form onSubmit={submit} className="mt-10 rounded-lg border border-[#222D3D] bg-[#07090E]/90 p-6 sm:p-8 space-y-8 fade-up" data-testid="property-signup-form">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] uppercase" style={{ color: accent }}>Property sign-up</p>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-50 mt-3">Set up AI agent monitoring for your property</h1>
            <p className="text-sm text-slate-400 mt-3 max-w-xl">Choose how you'd like to pay for your module. {brand ? `${brand.name} will help` : "Your installer will help"} install it on your property's network.</p>
          </div>
          <PlanPicker value={plan} onChange={setPlan} accent={accent} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="property-name" label="Property name" required value={form.property_name} onChange={set("property_name")} />
            <Field id="property-address" label="Property address" required value={form.property_address} onChange={set("property_address")} />
            <Field id="contact-name" label="Your name" required value={form.name} onChange={set("name")} />
            <Field id="contact-email" label="Email" type="email" required value={form.email} onChange={set("email")} />
            <Field id="contact-password" label="Password" type="password" required minLength={8} value={form.password} onChange={set("password")} hint="At least 8 characters." />
          </div>
          {error && <p className="text-sm text-rose-400" data-testid="property-signup-error">{error}</p>}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <p className="text-xs text-slate-500">You'll be taken to a secure payment page.</p>
            <Button type="submit" disabled={busy} className="font-semibold text-white hover:opacity-90" style={{ background: accent }} data-testid="property-signup-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to payment"}
            </Button>
          </div>
        </form>
        <p className="mt-6 text-sm text-slate-500" data-testid="property-signin-link">Already signed up? <Link to={slug ? `/login?p=${slug}` : "/login"} className="text-slate-200 underline underline-offset-4">Sign in</Link></p>
        {!brand && <p className="mt-2 text-sm text-slate-500" data-testid="signup-msp-link-row">Managed service provider? <Link to="/partners" className="text-slate-200 underline underline-offset-4" data-testid="signup-msp-link">Apply as an MSP partner</Link></p>}
      </div>
    </div>
  );
}
